import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type RefObject } from 'react';

export type ListingRowId = string | number;

interface ListingTableFocusOptions<T> {
  rows: T[];
  getRowId: (row: T) => ListingRowId;
  containerRef: RefObject<HTMLDivElement | null>;
  rowHeight?: number;
}

export const useListingTableFocus = <T,>({
  rows,
  getRowId,
  containerRef,
  rowHeight,
}: ListingTableFocusOptions<T>) => {
  const [focusedRowId, setFocusedRowId] = useState<ListingRowId | null>(null);

  const rowIds = useMemo(() => rows.map(getRowId), [rows, getRowId]);

  useEffect(() => {
    if (focusedRowId == null) {
      return;
    }
    if (!rowIds.includes(focusedRowId)) {
      setFocusedRowId(null);
    }
  }, [focusedRowId, rowIds]);

  const scrollFocusedRowIntoView = useCallback(
    (rowId: ListingRowId) => {
      const container = containerRef.current;
      if (!container) return;
      const rowEl = container.querySelector(`tr[data-row-id="${rowId}"]`) as HTMLElement | null;
      const tableEl = container.querySelector('.flowbite-listing-table') as HTMLElement | null;
      const headerGroup = tableEl?.querySelector('thead') as HTMLElement | null;
      const headerHeight = headerGroup?.offsetHeight ?? headerGroup?.getBoundingClientRect().height ?? 0;
      const padding = 6;
      const viewBottom = container.scrollTop + container.clientHeight - padding;

      if (rowEl) {
        const containerRect = container.getBoundingClientRect();
        const rowRect = rowEl.getBoundingClientRect();
        const rowTopInView = rowRect.top - containerRect.top;
        const rowBottomInView = rowRect.bottom - containerRect.top;
        const viewTopInView = Math.max(headerHeight, 0) + padding;

        if (rowTopInView < viewTopInView) {
          const delta = viewTopInView - rowTopInView;
          container.scrollTo({ top: Math.max(container.scrollTop - delta, 0), behavior: 'auto' });
          return;
        }
        if (rowBottomInView > container.clientHeight - padding) {
          const delta = rowBottomInView - (container.clientHeight - padding);
          container.scrollTo({ top: container.scrollTop + delta, behavior: 'auto' });
        }
        return;
      }

      if (typeof rowHeight === 'number' && rowHeight > 0) {
        const index = rowIds.findIndex((id) => id === rowId);
        if (index < 0) {
          return;
        }
        const rowTop = index * rowHeight;
        const rowBottom = rowTop + rowHeight;
        const containerRect = container.getBoundingClientRect();
        const viewTopOffset = Math.max(headerHeight, 0) + padding;
        const viewTop = container.scrollTop + viewTopOffset;
        if (rowTop < viewTop) {
          container.scrollTo({ top: Math.max(rowTop - viewTopOffset, 0), behavior: 'auto' });
          return;
        }
        if (rowBottom > viewBottom) {
          container.scrollTo({ top: rowBottom - container.clientHeight + padding, behavior: 'auto' });
        }
      }
    },
    [containerRef, rowHeight, rowIds]
  );

  const handleTableKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      const isFormField = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.tagName === 'SELECT';
      if (isFormField) {
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }
      event.preventDefault();
      if (rowIds.length === 0) {
        return;
      }
      const currentIndex = focusedRowId != null ? rowIds.findIndex((id) => id === focusedRowId) : -1;
      const nextIndex = event.key === 'ArrowDown'
        ? Math.min(currentIndex + 1, rowIds.length - 1)
        : Math.max(currentIndex - 1, 0);
      const nextRowId = rowIds[nextIndex];
      if (nextRowId == null) {
        return;
      }
      setFocusedRowId(nextRowId);
      scrollFocusedRowIntoView(nextRowId);
    },
    [focusedRowId, rowIds, scrollFocusedRowIntoView]
  );

  const handleRowClick = useCallback(
    (rowId: ListingRowId) => {
      setFocusedRowId(rowId);
      containerRef.current?.focus();
    },
    [containerRef]
  );

  return {
    focusedRowId,
    setFocusedRowId,
    handleTableKeyDown,
    handleRowClick,
  };
};
